import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { getAdminFeedback, getAdminStats, getAdminUsers } from './apiService';
import { sectionCardSx } from './designSystem';

function AdminPage() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [stats, setStats] = useState(null);
  const [feedbackList, setFeedbackList] = useState([]);
  const [usersList, setUsersList] = useState([]);

  const fetchAdminData = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      const [statsResponse, feedbackResponse, usersResponse] = await Promise.all([
        getAdminStats(),
        getAdminFeedback(),
        getAdminUsers(),
      ]);

      setStats(statsResponse);
      setFeedbackList(feedbackResponse.feedback || []);
      setUsersList(usersResponse.users || []);
    } catch (requestError) {
      setError(requestError.response?.data?.detail || 'Unable to load platform operations data.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAdminData();
  }, [fetchAdminData]);

  if (loading) {
    return (
      <Box sx={{ minHeight: '60vh', display: 'grid', placeItems: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container sx={{ py: { xs: 5, md: 7 } }}>
      <Stack spacing={3.5}>
        <Box>
          <Typography variant="overline" sx={{ color: '#13947F', letterSpacing: '0.14em', fontWeight: 800, fontSize: '0.72rem', textTransform: 'uppercase' }}>
            Platform Operations
          </Typography>
          <Typography variant="h2" sx={{ mt: 1.5, color: '#0B2E43', fontSize: { xs: '2rem', md: '2.7rem' }, letterSpacing: '-0.025em' }}>
            Internal visibility for the school product rollout.
          </Typography>
          <Typography sx={{ mt: 1.5, color: '#52677A', lineHeight: 1.8, maxWidth: 760 }}>
            Track adoption, feedback, and subscription posture for the internal team while the school-facing product matures.
          </Typography>
        </Box>

        {error && <Alert severity="error">{error}</Alert>}

        {stats && (
          <Grid container spacing={3}>
            {[
              { label: 'Total users', value: stats.users.total, accent: '#13947F', tint: 'rgba(19,148,127,0.1)' },
              { label: 'Paid workspaces', value: stats.users.focused + stats.users.pro, accent: '#46729B', tint: 'rgba(70,114,155,0.1)' },
              { label: 'Product feedback', value: stats.feedback.total, accent: '#C08A2D', tint: 'rgba(192,138,45,0.1)' },
            ].map((stat) => (
              <Grid item xs={12} md={4} key={stat.label}>
                <Card
                  sx={{
                    ...sectionCardSx,
                    position: 'relative',
                    overflow: 'hidden',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      height: 4,
                      background: `linear-gradient(90deg, ${stat.accent}, transparent 85%)`,
                    },
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Typography
                      sx={{
                        color: '#52677A',
                        fontWeight: 700,
                        fontSize: '0.74rem',
                        letterSpacing: '0.08em',
                        textTransform: 'uppercase',
                      }}
                    >
                      {stat.label}
                    </Typography>
                    <Typography
                      sx={{
                        color: stat.accent,
                        mt: 1,
                        fontWeight: 800,
                        fontSize: '2.2rem',
                        lineHeight: 1.1,
                        fontVariantNumeric: 'tabular-nums',
                        letterSpacing: '-0.02em',
                      }}
                    >
                      {stat.value}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}

        <Card sx={{ ...sectionCardSx }}>
          <CardContent sx={{ p: { xs: 3, md: 4 } }}>
            <Typography variant="h4" sx={{ color: '#0B2E43', mb: 2 }}>
              Feedback Queue
            </Typography>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Email</TableCell>
                    <TableCell>Rating</TableCell>
                    <TableCell>Feedback</TableCell>
                    <TableCell>Submitted</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {feedbackList.map((feedback) => (
                    <TableRow key={feedback._id}>
                      <TableCell>{feedback.email || feedback.username || 'Anonymous'}</TableCell>
                      <TableCell>{feedback.rating}</TableCell>
                      <TableCell>{feedback.feedback}</TableCell>
                      <TableCell>{new Date(feedback.submitted_at).toLocaleString()}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>

        <Card sx={{ ...sectionCardSx }}>
          <CardContent sx={{ p: { xs: 3, md: 4 } }}>
            <Typography variant="h4" sx={{ color: '#0B2E43', mb: 2 }}>
              User Directory
            </Typography>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>User</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Plan</TableCell>
                    <TableCell>Admin</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {usersList.map((user) => (
                    <TableRow key={user._id}>
                      <TableCell>{user.username}</TableCell>
                      <TableCell>{user.email}</TableCell>
                      <TableCell>{user.subscription}</TableCell>
                      <TableCell>{user.is_admin ? 'Yes' : 'No'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </Stack>
    </Container>
  );
}

export default AdminPage;
